import { Box, Button, createStyles, Group, Icon, Modal, Tabs, TFText, Tooltip, useDisclosure } from '@tf/ui';
import { D, S } from '@tf/utils';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProfileKindPossibleConnections } from '@/components/profiles/useProfileKindPossibleConnections';
import { ConfirmationModal, CounterBadge } from '@/components/shared';
import { AssignedAccountManagersWidget } from '@/components/shared/AssignedManagers';
import { useProfileCasesCount } from '@/core/api/caseRequests';
import { useOverviewAccount } from '@/core/api/overview';
import { useCreateProfileReviewRequestMutation } from '@/core/api/profiles';
import { useAccountInfoSegments, useAccountParams, useEntityConnectionLabels, useSelectedAccount } from '@/core/hooks';
import { UserCapabilities } from '@/core/types';
import { useCurrentUserCapabilityCheck } from '@/core/utils';

interface RouteBinding {
	path: string;
	label: string;
	count?: number;
}

const useStyles = createStyles(({ colors, breakpoints }) => ({
	root: {
		display: 'flex',
		alignItems: 'center',
		maxWidth: breakpoints.xl,
		margin: '0 auto',
		color: colors.gray[7],
		borderWidth: '0 0 1px 0',
		borderStyle: 'solid',
		borderColor: colors.gray[2],
	},
	tab: {
		display: 'flex',
		gap: '.35rem',
	},
	requestReviewButton: {
		marginLeft: 'auto',
		flexShrink: 0,
	},
}));

export const AccountTabs = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const { classes } = useStyles();
	const { accountId } = useAccountParams();
	const account = useSelectedAccount();
	const { nextKycReviewDate, accountStatus } = useAccountInfoSegments();
	const createReviewRequestMutation = useCreateProfileReviewRequestMutation();

	const connectionLabels = useEntityConnectionLabels(account.meta.entityKind);

	const connections = account.meta.listConnections.filter((c) => c.isReview);
	const path = `/${location.pathname.split('/').slice(3, 4).join('/')}`;
	const { data: clientCaseCount } = useProfileCasesCount(account.profileId);
	const withPaymentsCapability = useCurrentUserCapabilityCheck(UserCapabilities.CAN_REVIEW_PAYMENTS);
	const canPrepareAccountsData = useCurrentUserCapabilityCheck(UserCapabilities.CAN_PREPARE_ACCOUNTS_DATA);

	const possibleConnections = useProfileKindPossibleConnections(account.meta.entityKind);

	const shouldShowRequestReviewButton = () => {
		if (!canPrepareAccountsData) {
			return false;
		}

		if (!nextKycReviewDate || accountStatus === 'REJECTED' || accountStatus === 'CLOSED') {
			return false;
		}

		const daysOverdue = D.getOverdueDaysCount(nextKycReviewDate);
		const daysUntilDue = D.getDaysUntilDate(nextKycReviewDate);
		return daysOverdue > 0 || daysUntilDue <= 30;
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_pendingTimeLeft, setPendingTimeLeft] = React.useState(0);
	const [requestStatus, setRequestStatus] = React.useState<'idle' | 'pending' | 'sent'>('idle');
	const reviewModal = useDisclosure();

	const handleRequestReview = () => {
		reviewModal.open();
	};

	const handleConfirmReview = () => {
		setRequestStatus('pending');
		createReviewRequestMutation.mutate(
			{ profile: account.profileId },
			{
				onSuccess: () => {
					const countdown = setInterval(() => {
						setPendingTimeLeft((prev) => {
							if (prev <= 1) {
								clearInterval(countdown);
								setRequestStatus('sent');
								return 0;
							}
							return prev - 1;
						});
					}, 1000);
					setPendingTimeLeft(5);
					reviewModal.close();
				},
				onError: (error) => {
					console.error('Failed to create review request:', error);
					setRequestStatus('idle');
					setPendingTimeLeft(0);
					reviewModal.close();
				},
			},
		);
	};

	const routeBindings: RouteBinding[] = [
		{
			path: '/',
			label: 'Overview',
		},
		...(connections || [])
			// Sort by connectionKind to have same order in different locales
			.sort((a, b) => a.connectionKind.localeCompare(b.connectionKind))
			.map(({ connectionKind: kind }) => {
				const count = account.reviews.filter((r) => r.connectionKind === kind).length;

				return {
					path: `/${S.slugify(kind)}`,
					label: connectionLabels[kind].plural,
					count,
				};
			}),
	];

	if (possibleConnections.connections.length > 0) {
		routeBindings.push({
			label: possibleConnections.label,
			path: '/connections',
		});
	}

	routeBindings.push({
		path: '/cases',
		label: 'Cases',
		count: clientCaseCount,
	});

	if (withPaymentsCapability) {
		routeBindings.push({
			label: 'Payments',
			path: '/payments',
		});
	}

	return (
		<Box className={classes.root}>
			<Tabs
				variant="normal"
				value={path}
				styles={({ colors }) => ({
					root: { flexGrow: 1 },
					tab: {
						padding: '.65rem 1rem',
						borderBottomWidth: 1.5,
						borderRadius: 0,
						"&[data-active='true']": {
							fontWeight: 500,
							color: colors.brand[6],
							backgroundColor: colors.brand[0],
							borderBottom: `1.5px solid ${colors.brand[6]}`,
						},
					},
					list: { borderBottomWidth: 0 },
				})}
				onChange={(value) => navigate(`/accounts/${accountId}${value}`)}
			>
				<Tabs.List>
					{routeBindings.map(({ path, label, count }) => {
						return (
							<Tabs.Tab key={path} value={path}>
								<Box className={classes.tab}>
									{label}
									{count ? <CounterBadge value={count} /> : null}
								</Box>
							</Tabs.Tab>
						);
					})}
				</Tabs.List>
			</Tabs>
			{shouldShowRequestReviewButton() && (
				<Box style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
					<TFText size="sm" fw={500} c="#60A5FA">
						{requestStatus === 'sent'
							? 'Profile was sent for review.'
							: requestStatus === 'pending'
								? 'Sending...'
								: 'Profile needs review'}
					</TFText>
					<Tooltip label="Send to compliance review" position="left">
						<Button
							variant="light"
							size="sm"
							className={classes.requestReviewButton}
							onClick={handleRequestReview}
							disabled={requestStatus === 'pending' || createReviewRequestMutation.isPending}
							loading={createReviewRequestMutation.isPending}
							leftSection={<Icon.IconSend size={14} />}
						>
							{requestStatus === 'sent' ? 'Resend' : 'Send'}
						</Button>
					</Tooltip>
				</Box>
			)}
			<ReviewAssignmentModal
				isOpen={reviewModal.isOpen}
				onClose={reviewModal.close}
				onConfirm={handleConfirmReview}
				accountId={account.meta.graphNodeId}
			/>
		</Box>
	);
};

interface ReviewAssignmentModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	accountId: number;
}

const ReviewAssignmentModal: React.FC<ReviewAssignmentModalProps> = ({ isOpen, onClose, onConfirm, accountId }) => {
	const { data } = useOverviewAccount(accountId);
	const listUsers = data?.listUsers;
	const assignedManagers = data?.account.assignedManagers ?? [];

	const handleConfirm = () => {
		onConfirm();
	};

	return (
		<Modal opened={isOpen} onClose={onClose} title="Action confirmation">
			<ConfirmationModal
				message="Please, assign the account to a compliance manager or approve current managers before sending it to review."
				confirmText="Send to review"
				isSubmitDisabled={!assignedManagers.length}
				onConfirm={handleConfirm}
				onCancel={onClose}
			>
				<Group p="0.75rem 0 0 0" mih="44px">
					<TFText>Assigned managers:</TFText>
					<AssignedAccountManagersWidget
						accountId={accountId}
						selectedIds={assignedManagers}
						managers={listUsers ?? []}
						managersFilter={(m) => {
							return m.listRoleCapabilities.some((capability) => {
								return capability === UserCapabilities.CAN_REVIEW_ACCOUNTS_DATA;
							});
						}}
					/>
				</Group>
			</ConfirmationModal>
		</Modal>
	);
};
